// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Facility } from '../types';
import { num, esc, STATE_ORDER, STATE_NAME, stateColour } from '../format';
import { tipAttr } from '../charts';
import { squarify } from '../utils/squarify';
import { attachSvgZoom } from '../utils/svgZoom';

export function renderPrisons(root: HTMLElement, data: Dataset): void {
  const { prisons, meta } = data;
  const facilities = prisons.facilities;
  const c = meta.counts;
  const statesWith = STATE_ORDER.filter((s) => prisons.stateTotals[s]);

  root.innerHTML = `
    <div class="view-head">
      <h2>Where prisoners are held</h2>
      <p>The ${num(facilities.length)} correctional facilities counted on 30 June ${meta.latestYear}, sized by how many people each held. Australia's largest is ${c.biggestFacility ? `<strong>${esc(c.biggestFacility.name)}</strong> in ${esc(STATE_NAME[c.biggestFacility.state] ?? c.biggestFacility.state)} (${num(c.biggestFacility.total)})` : '—'}. Counts are of prisoners in each facility, not its capacity.</p>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Facilities by size</h3><p>Rectangle area is the number of prisoners; colour is the state or territory.</p></div>
      <div class="chart-wrap" id="pri-tree"></div>
      <div class="legend" id="pri-legend">${statesWith.map((s) => `<span class="legend-item"><span class="legend-swatch" style="background:${stateColour(s)}"></span>${esc(s)} · ${num(prisons.stateTotals[s])}</span>`).join('')}</div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Every facility</h3><p>Search by name or state.</p></div>
      <div class="controls"><input type="search" id="pri-search" placeholder="Search facilities…" aria-label="Search facilities" /></div>
      <div class="table-scroll">
        <table>
          <thead><tr><th>Facility</th><th>State</th><th class="right">Men</th><th class="right">Women</th><th class="right">Total</th></tr></thead>
          <tbody id="pri-tbody"></tbody>
        </table>
      </div>
    </div>
  `;

  drawTree(root.querySelector('#pri-tree') as HTMLElement, facilities);

  const tbody = root.querySelector('#pri-tbody') as HTMLElement;
  const rowsFor = (q: string) => {
    const needle = q.trim().toLowerCase();
    const list = facilities.filter((f) => !needle || f.name.toLowerCase().includes(needle) || f.state.toLowerCase().includes(needle) || (STATE_NAME[f.state] ?? '').toLowerCase().includes(needle));
    tbody.innerHTML = list.length
      ? list.map((f) => `<tr>
          <td class="region-name">${esc(f.name)}</td>
          <td><span class="state-pill" style="background:${stateColour(f.state)}22;color:${stateColour(f.state)}">${esc(f.state)}</span></td>
          <td class="right num">${num(f.males)}</td>
          <td class="right num">${num(f.females)}</td>
          <td class="right num">${num(f.total)}</td>
        </tr>`).join('')
      : `<tr><td colspan="5" class="empty-state">No facilities match “${esc(q)}”.</td></tr>`;
  };
  rowsFor('');

  let t: number | undefined;
  (root.querySelector('#pri-search') as HTMLInputElement).addEventListener('input', (e) => {
    window.clearTimeout(t);
    const v = (e.target as HTMLInputElement).value;
    t = window.setTimeout(() => rowsFor(v), 300);
  });
}

function drawTree(el: HTMLElement, facilities: Facility[]): void {
  const W = 900, H = 460;
  const rects = squarify(facilities.map((f) => f.total), W, H);
  let cells = '';
  rects.forEach((r, i) => {
    const f = facilities[i];
    const big = r.w > 78 && r.h > 30;
    cells += `<g ${tipAttr(`${f.name}\n${STATE_NAME[f.state] ?? f.state}\n${num(f.total)} prisoners (${num(f.males)} men, ${num(f.females)} women)`)} aria-label="${esc(f.name)}: ${num(f.total)}">
      <rect class="cell" x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${r.w.toFixed(1)}" height="${r.h.toFixed(1)}" fill="${stateColour(f.state)}" fill-opacity="0.86" stroke="#fff" stroke-width="1.5"/>
      ${big ? `<text x="${(r.x + 6).toFixed(1)}" y="${(r.y + 16).toFixed(1)}" style="font-size:11px;font-weight:600;fill:#fff">${esc(f.name.length > 24 ? f.name.slice(0, 22) + '…' : f.name)}</text>
      <text x="${(r.x + 6).toFixed(1)}" y="${(r.y + 31).toFixed(1)}" class="num" style="font-size:11px;fill:#ffffffcc">${num(f.total)}</text>` : ''}
    </g>`;
  });
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Treemap of correctional facilities by size">${cells}</svg>`;
  const svg = el.querySelector('svg') as SVGSVGElement | null;
  // viewBox.baseVal is present in real browsers; guard so headless/jsdom doesn't throw.
  if (svg && svg.viewBox?.baseVal?.width) attachSvgZoom(svg, { maxScale: 10 });
}
